import React, { useState, useEffect } from 'react';
import { Spin, Modal } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { ImageFallback } from '../../data/constants';
import { useImageTokenUrl } from '../../utils/imageutils';

interface ImageWithSpinnerProps {
    imageUrl?: string;
    title?: string;
}

/**
 * Global toggle to enable/disable SAS tokenization for image URLs.
 * Set to `true` to fetch and append the SAS token for images.
 * Set to `false` (default) to bypass tokenization and render raw database URLs.
 */
const ENABLE_TOKENIZATION = true;

/**
 * Renders an image inside a container with a loading spinner indicator.
 * Clicking the image opens a high-resolution modal preview.
 * Falls back to a placeholder image if the target URL fails to load.
 */
const ImageWithSpinner: React.FC<ImageWithSpinnerProps> = ({ imageUrl, title = '' }) => {
    const displayUrl = useImageTokenUrl(imageUrl, ENABLE_TOKENIZATION);
    const [imageLoading, setImageLoading] = useState(true); 
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    useEffect(() => {
        setImageLoading(true);
    }, [displayUrl]);

    const handleImageLoad = () => {
        setTimeout(() => {
            setImageLoading(false);
        }, 100);
    };

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        setTimeout(() => {
            setImageLoading(false);
        }, 100);
        e.currentTarget.onerror = null;
        e.currentTarget.src = ImageFallback;
    };

    return (
        <>
            {title ? (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                    <div style={{ flex: 1, minHeight: 0, position: 'relative', height: '100%' }}>
                        {imageLoading && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    width: '100%',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                }}
                            >
                                <Spin indicator={<LoadingOutlined spin />} size="large" />
                            </div>
                        )}
                        <img
                            src={displayUrl || ImageFallback}
                            alt={title}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',
                                display: imageLoading ? 'none' : 'block',
                                cursor: displayUrl ? 'pointer' : 'default',
                            }}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                            onClick={() => {
                                if (displayUrl) {
                                    setPreviewVisible(true);
                                    setPreviewImage(displayUrl);
                                }
                            }}
                        />
                    </div>
                    <div
                        style={{
                            marginTop: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: '#333',
                            padding: '2px 5px',
                            wordWrap: 'break-word',
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                        title={title}
                    >
                        {title}
                    </div>
                </div>
            ) : (
                <>
                    {imageLoading && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                            }}
                        >
                            <Spin indicator={<LoadingOutlined spin />} size="large" />
                        </div>
                    )}
                    <img
                        src={displayUrl || ImageFallback}
                        alt={title}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',
                            display: imageLoading ? 'none' : 'block',
                            cursor: displayUrl ? 'pointer' : 'default',
                        }}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                        onClick={() => {
                            if (displayUrl) {
                                setPreviewVisible(true);
                                setPreviewImage(displayUrl);
                            }
                        }}
                    />
                </>
            )}
            <Modal
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                style={{ marginTop: 20 }}
            >
                <div style={{ overflow: 'auto', maxHeight: '80vh' }}>
                    <img alt="Preview" style={{ width: '100%' }} src={previewImage} />
                </div>
            </Modal>
        </>
    );
};

export default ImageWithSpinner;